import {inject, Injectable, LOCALE_ID} from '@angular/core';
import {DatePipe} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {OfferDTO, SalesPersonDTO} from '../shared/model/bench-entry.model';
import {DateService} from './date.service';

/** Nachschlagewerk über die Sales-Liste: einmal je Ladevorgang bauen, dann je Angebot auflösen. */
export interface SalesIndex {
  byAbbreviation: Map<string, SalesPersonDTO>;
  byUserId: Map<string, SalesPersonDTO>;
}

export function buildSalesIndex(sales: SalesPersonDTO[]): SalesIndex {
  return {
    byAbbreviation: new Map((sales ?? []).map(s => [s.abbreviation, s])),
    byUserId: new Map((sales ?? []).map(s => [s.userId, s])),
  };
}

/**
 * Kürzel der Sales-Liste, alphabetisch — Vorrat für die Filter-Dropdowns (NP20-924).
 *
 * `additional` nimmt Kürzel auf, die nur noch an Angeboten hängen, weil die Person nicht mehr in
 * der Liste steht: ohne sie zeigt die Tabelle ein Kürzel, das der Filter nicht kennt.
 */
export function salesAbbreviations(sales: SalesPersonDTO[], additional: string[] = []): string[] {
  return [...new Set([...(sales ?? []).map(s => s.abbreviation), ...additional].filter(a => !!a))]
    .sort((a, b) => a.localeCompare(b));
}

/** Wer betreut dieses Angebot gerade (NP20-924). */
export interface SalesSubstitution {
  /** Kürzel der anbietenden Person; leer, wenn am Angebot keines steht. */
  original: string;
  /** Aktuell zuständiges Kürzel — der Vertreter während der Abwesenheit, sonst `original`. */
  active: string;
  /** true, solange die anbietende Person abwesend ist und ein auflösbarer Vertreter hinterlegt ist. */
  substituted: boolean;
  /** Hover-Text mit Grund und Enddatum; leer ohne Vertretung. */
  tooltip: string;
}

/** Alles, was zur Auflösung der anbietenden Person eines Angebots gebraucht wird. */
type OfferSubmitter = Pick<OfferDTO, 'submittedBy' | 'submittedByUserId'>;

/**
 * Löst die aktive Vertretung eines Angebots auf (NP20-924). Sales-View, Detailansicht und der
 * Sales-Filter der Übersicht müssen dieselbe Ableitung nutzen — vorher hatte jede Ansicht ihre
 * eigene, und die Anzeige fand Vertretungen, die der Filter nicht kannte.
 *
 * Bewusst nur **eine** Stufe: ist der Vertreter selbst abwesend, bleibt trotzdem er zuständig. Das
 * entspricht der Festlegung im Backend (NP20-792), eine Vertreter-Kaskade gibt es nicht.
 */
@Injectable({providedIn: 'root'})
export class SalesSubstitutionService {
  private readonly dateService = inject(DateService);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = new DatePipe(inject(LOCALE_ID));

  resolve(offer: OfferSubmitter, index: SalesIndex): SalesSubstitution {
    const submitter = this.submitterOf(offer, index);
    // NP20-765: Das Kürzel aus der Sales-Liste gewinnt, damit eine Umbenennung in der
    // Benutzerverwaltung auch an Altangeboten ankommt; das gespeicherte bleibt der Rückfall.
    const original = submitter?.abbreviation ?? offer.submittedBy ?? '';
    const unchanged: SalesSubstitution = {original, active: original, substituted: false, tooltip: ''};

    if (!submitter?.substituteUserId || !this.dateService.isSalesMemberAbsent(submitter)) {
      return unchanged;
    }
    // Steht der Vertreter nicht (mehr) in der Liste, ist sein Kürzel unbekannt. Dann bleibt die
    // Anzeige unverändert, statt eine rohe userId als Kürzel auszugeben.
    const deputy = index.byUserId.get(submitter.substituteUserId);
    if (!deputy?.abbreviation) {
      return unchanged;
    }
    return {
      original,
      active: deputy.abbreviation,
      substituted: true,
      tooltip: this.tooltipFor(submitter, original, deputy),
    };
  }

  /**
   * Trifft das Angebot den Filter auf ein Kürzel? Sowohl die eigenen Angebote der Person — auch
   * während ihrer Abwesenheit — als auch die, die sie gerade vertretungsweise betreut.
   */
  offerMatches(offer: OfferSubmitter, abbreviation: string, index: SalesIndex): boolean {
    const {original, active} = this.resolve(offer, index);
    return original === abbreviation || active === abbreviation;
  }

  private submitterOf(offer: OfferSubmitter, index: SalesIndex): SalesPersonDTO | undefined {
    return offer.submittedByUserId
      ? index.byUserId.get(offer.submittedByUserId)
      : index.byAbbreviation.get(offer.submittedBy);
  }

  private tooltipFor(absent: SalesPersonDTO, original: string, deputy: SalesPersonDTO): string {
    const params = {
      deputy: deputy.abbreviation,
      deputyName: deputy.fullName || deputy.abbreviation,
      sales: original,
      reason: absent.absenceReason || this.translate.instant('benchOverview.sales.absenceDefault'),
      until: this.datePipe.transform(absent.absenceUntil, 'dd.MM.yyyy') ?? '',
    };
    // Ohne „bis" läuft die Abwesenheit offen weiter — dann darf im Hover kein leeres Datum stehen.
    const key = params.until ? 'benchOverview.sales.deputyTooltip' : 'benchOverview.sales.deputyTooltipOpen';
    return this.translate.instant(key, params);
  }
}
